import {IUsageDateType} from 'interfaces/interface';

/**
 * 메뉴 데이터 타입
 * 메뉴 구조를 정의하는 타입입니다.
 */
export type IMenuData = {[key: string]: string[] | null};

/**
 * 현장 기본 메뉴 데이터
 * 현장에서 사용하는 기본 메뉴 구조입니다.
 */
export const basicMenuData: IMenuData = {
  siteManagement: [
    'siteInfo',
    'siteOperatorStatus',
    'sitePartnerStatus',
    'approvalSettings',
    'paymentManagement',
    'specialSettings',
  ],
  commuteManagement: ['commuteStatus', 'departureSafeStatus', 'commuteRecords'],
  workerManagement: [
    'workerListDocuments',
    'workerHealthInfoManagement',
    'workerTeamJobType',
  ],
  safetyEducationManagement: null,
  riskAssessment: ['riskAssessmentSub', 'riskAssessmentMinutes'],
  TBM: null,
  safetyWorkPermit: null,
  workReport: null,
  safetyInspection: null,
  announcements: null,
  emergencySiren: null,
  workerFeedback: null,
  machineManagement: null,
};

/**
 * 시공사 관리자 기본 메뉴 데이터
 * 시공사 관리자가 사용하는 기본 메뉴 구조입니다.
 */
export const managerBasicMenuData: IMenuData = {
  contractorManagement: [
    'constructionInfoManagement',
    'contractorJointOperatorStatus',
    'contractorAddTeam',
  ],
  partnerManagement: ['contractorPartnerStatus', 'partnerAddTeam'],
  viewSiteList: [
    'currentActiveSites',
    'terminatedSites',
    'siteApplicationApproval',
  ],
};

/**
 * 시공사 운영관리자, 공동운영관리자 전체현장 메뉴 데이터
 * 시공사 운영관리자와 공동운영관리자가 전체 현장에서 사용하는 메뉴 구조입니다.
 */
export const ownerAllMenuData: IMenuData = {
  ...managerBasicMenuData,
  siteComparisonEvaluation: null,
  headquartersNotifications: null,
  overallAttendanceManagement: null,
  overallEmployeeManagement: null,
  overallSafetyEducationManagement: null,
  overallRiskAssessment: null,
  overallTBM: null,
  contractorWithdrawalRequest: null,
};

/**
 * 시공사 관리자 개별현장 메뉴 데이터
 * 시공사 관리자가 개별 현장에서 사용하는 메뉴 구조입니다.
 */
export const managerBasicEachMenuData: IMenuData = {
  ...managerBasicMenuData,
  ...basicMenuData,
};

/**
 * 협력사 공동운영관리자, 관리자 전체현장 메뉴 데이터
 * 협력사 공동운영관리자와 관리자가 전체 현장에서 사용하는 메뉴 구조입니다.
 */
export const partnerAllMenuData: IMenuData = {
  viewSiteList: ['currentActiveSites', 'terminatedSites'],
};

/**
 * 협력사 관리자 개별현장 메뉴 데이터
 * 협력사 관리자가 개별 현장에서 사용하는 메뉴 구조입니다.
 */
export const partnerEachMenuData: IMenuData = {
  siteManagement: [
    'siteInfo',
    'addPartnerManager',
    'approvalSettings',
    'partnerAddTeam',
  ],
  commuteManagement: ['commuteStatus', 'departureSafeStatus', 'commuteRecords'],
  workerManagement: [
    'workerListDocuments',
    'workerHealthInfoManagement',
    'workerTeamJobType',
  ],
  safetyEducationManagement: null,
  riskAssessment: ['riskAssessmentSub', 'riskAssessmentMinutes'],
  TBM: null,
  safetyWorkPermit: null,
  announcements: null,
  emergencySiren: null,
  workerFeedback: null,
  machineManagement: null,
};

/**
 * 시공사 운영관리자, 공동운영관리자 전용 메뉴 목록
 * 시공사 운영관리자와 공동운영관리자만 접근 가능한 메뉴 목록입니다.
 */
export const onlyContractorOwnerAccessMenuList = [
  'siteComparisonEvaluation',
  'headquartersNotifications',
  'overallAttendanceManagement',
  'overallEmployeeManagement',
  'overallSafetyEducationManagement',
  'overallRiskAssessment',
  'overallTBM',
  'contractorWithdrawalRequest',
];

/**
 * 시공사 관리자 전용 메뉴 목록
 * 시공사 관리자만 접근 가능한 메뉴 목록입니다.
 */
export const onlyContractorManagerAccessMenuList = [
  'constructionInfoManagement',
  'contractorJointOperatorStatus',
  'contractorAddTeam',
  'contractorPartnerStatus',
  'assignPartnerAdministrator',
  'partnerAddTeam',
  'currentActiveSites',
  'terminatedSites',
  'siteApplicationApproval',
];

/**
 * 백오피스 메뉴 데이터
 * 백오피스에서 사용하는 메뉴 구조입니다.
 */
export const menuData: IMenuData = {
  fieldManagement: ['onSiteCreationRequest', 'siteStateManagement'],
  noticeAndInspection: ['systemCheck', 'onSiteNotice'],
  salesManagement: null,
  paymentManagement: ['paymentFailure'],
  usage: null,
  userManagement: ['userManagementTongPlace', 'userManagementUser', 'headquartersAccessControl'],
  partnerCompany: null,
  contractorManagement: null,
  contractorStatistics: null,
  operationsAnalytics: null,
  changeHistory: null,
  // referralCode: ['referralCodeList'],
};

/**
 * 사용량 조회 날짜 타입 목록
 * 사용량 조회 시 사용할 수 있는 날짜 타입 목록입니다.
 */
export const usageDateTypeList = [
  'yearly',
  'monthly',
  'daily',
] as IUsageDateType[];

/**
 * 날짜 타입별 사용량 API URL 매핑
 * 날짜 타입에 따라 사용할 API URL을 매핑한 객체입니다.
 */
export const usageDateTypeApiUrl = {
  yearly: 'yau',
  monthly: 'mau',
  daily: 'dau',
};
